import React from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import Grain from "../components/Grain";

function formatDateFR(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
}

export default function AtelierMerci() {
  const { reservationId } = useParams();
  const [params] = useSearchParams();
  const stripeSessionId = params.get("session_id");
  const [status, setStatus] = React.useState("checking");
  const [reservation, setReservation] = React.useState(null);
  const [attempts, setAttempts] = React.useState(0);
  const [retryToken, setRetryToken] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    // Apple Pay + Stripe Live can take 60-120s. Poll for up to 90 attempts × 2s = 3 min.
    const MAX_ATTEMPTS = 90;
    const poll = async (n = 0) => {
      if (n >= MAX_ATTEMPTS || cancelled) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      try {
        if (stripeSessionId) {
          const r = await api.get(`/payments/status/${stripeSessionId}`);
          if (r.data.payment_status === "paid") {
            const res = await api.get(`/reservations/${reservationId}`);
            if (!cancelled) {
              setReservation(res.data);
              setStatus("paid");
            }
            return;
          }
          if (r.data.status === "expired") {
            if (!cancelled) setStatus("expired");
            return;
          }
        } else {
          const res = await api.get(`/reservations/${reservationId}`);
          if (res.data.statut === "paye") {
            if (!cancelled) { setReservation(res.data); setStatus("paid"); }
            return;
          }
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId, stripeSessionId, retryToken]);

  const manualRetry = () => {
    setAttempts(0);
    setStatus("checking");
    setRetryToken((t) => t + 1);
  };

  return (
    <div className="pt-28 section-dark min-h-screen flex items-center" data-testid="merci-page">
      <section className="px-6 md:px-12 py-20 w-full relative overflow-hidden">
        <Grain/>
        <div className="absolute inset-0 glow-amber-soft pointer-events-none"/>
        <div className="max-w-[900px] mx-auto text-center relative z-10">
          {status === "checking" && (
            <>
              <Loader2 size={40} className="animate-spin text-accent-deep mx-auto"/>
              <h1 className="font-serif text-4xl md:text-5xl italic mt-8">Confirmation en cours…</h1>
              <p className="mt-4 text-text-cream/60 font-light">Vérification de ton paiement ({attempts}/90)</p>
              <p className="mt-2 text-text-cream/40 font-light text-sm">Stripe et Apple Pay peuvent prendre jusqu'à 2 minutes pour confirmer.</p>
            </>
          )}
          {status === "paid" && reservation && (
            <>
              <motion.div initial={{ scale:0 }} animate={{ scale:1 }} transition={{ type:"spring", duration:0.8 }} className="mx-auto mb-8 w-20 h-20 flex items-center justify-center border-2 border-accent-deep rounded-full">
                <motion.div initial={{ pathLength:0 }} animate={{ pathLength:1 }} transition={{ delay:0.3, duration:0.6 }}>
                  <Check size={36} className="text-accent-deep" strokeWidth={1.5}/>
                </motion.div>
              </motion.div>
              <h1 className="h-display text-5xl md:text-7xl">Ta place est<br/><span className="italic-accent">confirmée.</span></h1>
              <div className="mt-10 border border-white/15 p-8 text-left max-w-md mx-auto">
                <div className="eyebrow mb-2 text-accent-light">Session</div>
                <div className="font-serif text-2xl italic">{formatDateFR(reservation.session.date)}</div>
                <div className="text-text-cream/60 text-sm font-light mt-1">
                  {reservation.session.heure_debut}–{reservation.session.heure_fin} · {reservation.session.lieu}
                </div>
                <div className="border-t border-white/10 pt-4 mt-4 flex justify-between items-baseline">
                  <span className="eyebrow">Montant payé</span>
                  <span className="font-serif text-2xl">CHF {reservation.montant_paye}</span>
                </div>
                {reservation.type_paiement === "acompte" && (
                  <div className="flex justify-between items-baseline mt-2">
                    <span className="eyebrow text-text-cream/50">Solde (7j avant)</span>
                    <span className="font-serif text-xl text-text-cream/60">CHF {reservation.montant_restant}</span>
                  </div>
                )}
              </div>
              <p className="mt-8 text-text-cream/70 font-light max-w-md mx-auto">
                Tu recevras un email de confirmation. Je t'enverrai les détails pratiques
                (adresse exacte, quoi apporter) 7 jours avant la séance.
              </p>
              <Link to="/" className="btn-primary mt-10 inline-flex">Retour à l'accueil <ArrowRight size={14}/></Link>
            </>
          )}
          {(status === "timeout" || status === "expired") && (
            <>
              <h1 className="font-serif text-4xl italic">Hmm, quelque chose s'est passé.</h1>
              <p className="mt-4 text-text-cream/60 font-light">
                {status === "expired"
                  ? "La session de paiement a expiré."
                  : "On n'a pas pu confirmer ton paiement dans le délai imparti."}
                <br/>Si tu as bien été débité(e), clique sur \"Vérifier à nouveau\" — ça peut prendre quelques secondes de plus côté Stripe.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <button onClick={manualRetry} className="btn-primary" data-testid="merci-retry">
                  Vérifier à nouveau
                </button>
                <Link to="/contact" className="btn-secondary">Me contacter</Link>
              </div>
              <p className="mt-8 text-text-cream/40 font-light text-xs">
                Référence : {reservationId}
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
